import { Toaster } from "@/components/ui/toaster";
import { AuthProvider, useAuth } from "@/features/auth/context/AuthContext";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes } from "react-router-dom";

// Auth pages
import AuthCallback from "@/features/auth/pages/AuthCallback";
import ForgotPassword from "@/features/auth/pages/ForgotPassword";
import Login from "@/features/auth/pages/Login";
import Logout from "@/features/auth/pages/Logout";
import Signup from "@/features/auth/pages/Signup";

// Application pages
import ApplicationForm from "@/features/applications/pages/ApplicationForm";
import ApplicationList from "@/features/applications/pages/ApplicationList";
import TalentList from "@/features/applications/pages/TalentList";

// Task pages
import TaskForm from "@/features/tasks/pages/TaskForm";
import TaskList from "@/features/tasks/pages/TaskList";

// Job pages
import MyJobs from "@/features/jobs/pages/MyJobs";

// Settings pages
import SettingsLayout from "@/features/settings/components/SettingsLayout";
import SettingsAppearance from "@/features/settings/pages/AppearanceSettingsPage";
import SettingsCompany from "@/features/settings/pages/CompanySettingsPage";
import SettingsNotifications from "@/features/settings/pages/NotificationSettingsPage";
import SettingsProfile from "@/features/settings/pages/ProfileSettingsPage";
import SettingsFacilities from "@/features/settings/pages/SettingsFacilities";
import SettingsProfiles from "@/features/settings/pages/SettingsProfiles";
import SettingsJobs from "@/features/settings/pages/SettingsJobs";
import SettingsSubsidiaries from "@/features/settings/pages/SettingsSubsidiaries";

// Administration pages (now moved to settings)
import CompanyManagement from "@/features/administration/pages/CompanyManagement";
import RequiredDocuments from "@/features/administration/pages/RequiredDocuments";
import UserManagement from "@/features/administration/pages/UserManagement";
import DocumentsList from "@/features/administration/pages/DocumentsList";

// Layout components
import { RoleGuard } from "@/components/common/RoleGuard";
import AuthLayout from "@/components/layouts/AuthLayout";
import MainLayout from "@/components/layouts/MainLayout";
import TalentLayout from "@/components/layouts/TalentLayout";

// Features
import { CookieScriptLoader } from "@/components/features/CookieScriptLoader";
import { CookieManagement } from "@/components/features/CookieManagement";

// Talent pages
import TalentProfile from "@/features/talent/pages/TalentProfile";
import TalentProfileView from "@/features/talent/pages/TalentProfileView";
import TalentExperience from "@/features/talent/pages/TalentExperience";
import TalentEducation from "@/features/talent/pages/TalentEducation";
import TalentGermanSkills from "@/features/talent/pages/TalentGermanSkills";
import TalentJobs from "@/features/talent/pages/TalentJobs";
import TalentPersonalInfo from "@/features/talent/pages/TalentPersonalInfo";
import TalentSettings from "@/features/talent/pages/TalentSettings";
import ExamSimulationPage from "@/features/exam-simulation/pages/ExamSimulationPage";

// TODO: Add not found page

function RootRedirect() {
  const { profile } = useAuth();

  if (!profile) {
    return <Navigate to="/login" replace />;
  }
  // Redirect based on user role
  if (["admin", "staff"].includes(profile.role) || profile.isSuperUser) {
    return <Navigate to="/applications" replace />;
  } else if (["partner"].includes(profile.role)) {
    return <Navigate to="/applicants" replace />;
  } else if (["talent"].includes(profile.role)) {
    return <Navigate to="/jobs" replace />;
  }
}

function TalentProtectedRoute({ children }: { children: React.ReactNode }) {
  const { profile } = useAuth();
  if (!profile || profile.anonymous) {
    return <Navigate to="/login" replace />;
  }

  // Admin/staff users should go to admin routes, everyone else to talent routes
  if (["admin", "staff"].includes(profile.role) || profile.isSuperUser) {
    return <Navigate to="/applications" replace />;
  }

  return <>{children}</>;
}

function AdminAndPartnerProtectedRoute({
  children
}: {
  children: React.ReactNode;
}) {
  const { profile } = useAuth();
  if (!profile) {
    return <Navigate to="/login" replace />;
  }
  // Only admin/staff/superuser roles can access admin routes
  if (
    !["admin", "staff", "partner"].includes(profile.role) &&
    !profile.isSuperUser
  ) {
    return <Navigate to="/jobs" replace />;
  }

  return <>{children}</>;
}

import LmsLayout from "@/features/lms/components/LmsLayout";
import CourseOverview from "@/features/lms/pages/CourseOverview";
import LessonView from "@/features/lms/pages/LessonView";

function TalentProfileRouteDispatcher() {
  const { profile } = useAuth();

  if (!profile) {
    return <Navigate to="/login" replace />;
  }

  // Admin/staff/partner users get the Admin layout
  if (
    ["admin", "staff", "partner"].includes(profile.role) ||
    profile.isSuperUser
  ) {
    return (
      <AdminAndPartnerProtectedRoute>
        <MainLayout>
          <TalentProfileView />
        </MainLayout>
      </AdminAndPartnerProtectedRoute>
    );
  }

  // Talent users get the Talent layout
  return (
    <TalentProtectedRoute>
      <TalentLayout>
        <TalentProfileView />
      </TalentLayout>
    </TalentProtectedRoute>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/signup/talent" element={<Signup />} />
        <Route path="/signup/employer" element={<Signup />} />
        <Route path="/signup/partner" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/logout" element={<Logout />} />
      </Route>

      {/* Auth callback for SSO */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Legacy talent auth redirects */}
      <Route path="/talent/login" element={<Navigate to="/login" replace />} />
      <Route
        path="/talent/forgot-password"
        element={<Navigate to="/forgot-password" replace />}
      />

      {/* Shared Talent Route Dispatcher */}
      <Route path="/talent/:id" element={<TalentProfileRouteDispatcher />} />

      {/* Talent routes (top level) */}
      <Route
        element={
          <TalentProtectedRoute>
            <TalentLayout />
          </TalentProtectedRoute>
        }
      >
        <Route path="/jobs" element={<TalentJobs />} />
        <Route path="/profile" element={<TalentProfile />} />
        <Route
          path="/profile/personal-info"
          element={<TalentPersonalInfo currentStep={1} totalSteps={4} />}
        />
        <Route
          path="/profile/german-skills"
          element={<TalentGermanSkills currentStep={2} totalSteps={4} />}
        />
        <Route
          path="/profile/experience"
          element={<TalentExperience currentStep={3} totalSteps={4} />}
        />
        <Route
          path="/profile/education"
          element={<TalentEducation currentStep={4} totalSteps={4} />}
        />
        <Route
          path="/your_application/:id"
          element={<ApplicationForm showBreadcrumbs={false} />}
        />
        <Route path="/account" element={<TalentSettings />} />
        <Route path="/account/manage-cookies" element={<CookieManagement />} />
        {/* LMS Routes (Talent Only) */}
        <Route path="/job-recognition-course" element={<LmsLayout />}>
          <Route index element={<CourseOverview />} />
          <Route path="lesson/:lessonId" element={<LessonView />} />
        </Route>
        <Route
          path="/exam-simulation"
          element={
            <RoleGuard redirectTo="/jobs">
              <ExamSimulationPage />
            </RoleGuard>
          }
        />
      </Route>

      {/* Admin routes */}
      <Route
        element={
          <AdminAndPartnerProtectedRoute>
            <MainLayout />
          </AdminAndPartnerProtectedRoute>
        }
      >
        <Route path="/" element={<RootRedirect />} />
        <Route path="/applications" element={<ApplicationList />} />
        <Route path="/applications/:id" element={<ApplicationForm />} />
        <Route path="/applicants" element={<TalentList />} />
        <Route path="/myjobs" element={<MyJobs />} />
        <Route path="/tasks" element={<TaskList />} />
        <Route path="/tasks/new" element={<TaskForm />} />
        <Route path="/tasks/:id/edit" element={<TaskForm />} />

        {/* Settings routes */}
        <Route path="/settings" element={<SettingsLayout />}>
          <Route index element={<Navigate to="profile" replace />} />
          {/* Personal settings - available to all users */}
          <Route path="profile" element={<SettingsProfile />} />
          <Route path="appearance" element={<SettingsAppearance />} />
          <Route path="notifications" element={<SettingsNotifications />} />
          {/* Company settings - admin and superuser only */}
          <Route
            path="company"
            element={
              <RoleGuard allowedRoles={["admin"]}>
                <SettingsCompany />
              </RoleGuard>
            }
          />
          <Route
            path="facilities"
            element={
              <RoleGuard allowedRoles={["admin"]}>
                <SettingsFacilities />
              </RoleGuard>
            }
          />
          <Route
            path="job-profiles"
            element={
              <RoleGuard allowedRoles={["admin"]}>
                <SettingsProfiles />
              </RoleGuard>
            }
          />
          <Route
            path="jobs"
            element={
              <RoleGuard allowedRoles={["admin"]}>
                <SettingsJobs />
              </RoleGuard>
            }
          />
          <Route
            path="subsidiaries"
            element={
              <RoleGuard allowedRoles={["admin"]}>
                <SettingsSubsidiaries />
              </RoleGuard>
            }
          />
          {/* Superuser administration - superuser only */}
          <Route
            path="companies"
            element={
              <RoleGuard>
                <CompanyManagement />
              </RoleGuard>
            }
          />
          <Route
            path="users"
            element={
              <RoleGuard>
                <UserManagement />
              </RoleGuard>
            }
          />
          <Route
            path="required-documents"
            element={
              <RoleGuard>
                <RequiredDocuments />
              </RoleGuard>
            }
          />
          <Route
            path="documents-list"
            element={
              <RoleGuard>
                <DocumentsList />
              </RoleGuard>
            }
          />
          <Route path="manage-cookies" element={<CookieManagement />} />
        </Route>
      </Route>
    </Routes>
  );
}

function App() {
  const { i18n } = useTranslation();

  // Update HTML lang attribute when language changes
  useEffect(() => {
    document.documentElement.lang = i18n.language;

    const handleLanguageChanged = (lng: string) => {
      document.documentElement.lang = lng;
    };

    i18n.on("languageChanged", handleLanguageChanged);
    return () => i18n.off("languageChanged", handleLanguageChanged);
  }, [i18n]);

  return (
    <AuthProvider>
      <CookieScriptLoader>
        <AppRoutes />
        <Toaster />
      </CookieScriptLoader>
    </AuthProvider>
  );
}

export default App;
